/**
 * RendPleX Scenario Analyzer 2.0 — P3C Deal Constraint Engine tab.
 *
 * User-facing question:
 *   "Can this deal meet my financial requirements within my limits?"
 *
 * Calls POST /api/scenario/constraints. Deterministic. Never
 * fabricates a solution: on `not_achievable`, renders the closest
 * hard-respecting scenario + unmet constraints + binding constraints.
 * No DB. No LLM.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import {
  ShieldCheck, Loader2, CheckCircle2, AlertTriangle,
  Wallet, TrendingUp, Lock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HARD_FIELDS = [
  { key: 'max_purchase_price',       i18n: 'constraints.maxPurchasePrice',      prefix: '$', step: 1000 },
  { key: 'max_cash_available',       i18n: 'constraints.maxCashAvailable',      prefix: '$', step: 1000 },
  { key: 'max_down_payment_percent', i18n: 'constraints.maxDownPaymentPercent', suffix: '%', step: 0.5 },
  { key: 'min_down_payment_percent', i18n: 'constraints.minDownPaymentPercent', suffix: '%', step: 0.5 },
];

const PERF_FIELDS = [
  { key: 'min_monthly_cash_flow', i18n: 'constraints.minMonthlyCashFlow', prefix: '$', step: 50 },
  { key: 'min_dscr',              i18n: 'constraints.minDscr',            step: 0.05 },
  { key: 'min_cap_rate',          i18n: 'constraints.minCapRate',         suffix: '%', step: 0.1 },
  { key: 'min_cash_on_cash',      i18n: 'constraints.minCashOnCash',      suffix: '%', step: 0.1 },
];

const BINDING_I18N = {
  max_cash_available:        'constraints.bindingMaxCash',
  max_purchase_price:        'constraints.bindingMaxPrice',
  max_down_payment_percent:  'constraints.bindingMaxDp',
  min_down_payment_percent:  'constraints.bindingMinDp',
  scenario_bound_price_min:  'constraints.bindingScenarioFloor',
  locked_purchase_price:     'constraints.bindingLockedPrice',
  locked_interest_rate:      'constraints.bindingLockedRate',
  locked_rent:               'constraints.bindingLockedRent',
  locked_down_payment:       'constraints.bindingLockedDp',
};

const METRIC_I18N = {
  monthly_cash_flow: 'constraints.metricMcf',
  dscr:              'constraints.metricDscr',
  cap_rate:          'constraints.metricCap',
  cash_on_cash:      'constraints.metricCoc',
  roi:              'constraints.metricRoi',
};

const fmtMoney = (n) => (n === null || n === undefined || Number.isNaN(n))
  ? '—' : `$${Math.round(n).toLocaleString()}`;
const fmtPct = (n, dec = 2) => (n === null || n === undefined || Number.isNaN(n))
  ? '—' : `${n.toFixed(dec)}%`;
const fmtRatio = (n, dec = 2) => (n === null || n === undefined || Number.isNaN(n))
  ? '—' : n.toFixed(dec);

const fmtMetric = (metricKey, value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (metricKey === 'monthly_cash_flow') return fmtMoney(value);
  if (metricKey === 'dscr') return fmtRatio(value);
  return fmtPct(value);
};

const ConstraintRow = ({ field, active, value, onToggle, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)_130px] gap-2 items-center py-1.5">
      <input
        data-testid={`constraint-toggle-${field.key}`}
        type="checkbox"
        checked={active}
        onChange={(e) => onToggle(e.target.checked)}
        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 accent-indigo-600"
      />
      <label
        htmlFor={`constraint-input-${field.key}`}
        className={`text-sm ${active ? 'text-slate-900 dark:text-slate-100' : 'text-slate-500 dark:text-slate-500'}`}
      >
        {t(field.i18n)}
      </label>
      <div className="relative">
        {field.prefix && (
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
        )}
        <input
          id={`constraint-input-${field.key}`}
          data-testid={`constraint-input-${field.key}`}
          type="number"
          step={field.step}
          disabled={!active}
          value={value ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            const n = raw === '' ? null : Number(raw);
            if (n === null || Number.isFinite(n)) onChange(n);
          }}
          className={`w-full h-8 rounded-md border text-xs font-mono tabular-nums ${
            field.prefix ? 'pl-6' : 'pl-2'
          } ${field.suffix ? 'pr-6' : 'pr-2'} focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
            active
              ? 'border-slate-300 dark:border-slate-700 bg-white dark:bg-[#0F1419] text-slate-900 dark:text-slate-100'
              : 'border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-slate-400'
          }`}
        />
        {field.suffix && (
          <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs ${active ? 'text-slate-500' : 'text-slate-400'}`}>
            {field.suffix}
          </span>
        )}
      </div>
    </div>
  );
};

const ConstraintResultRow = ({ result, satisfied }) => {
  const { t } = useTranslation();
  const Icon = satisfied ? CheckCircle2 : AlertTriangle;
  const cls = satisfied ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400';
  return (
    <div
      data-testid={`constraint-result-${result.id}`}
      className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 dark:border-slate-800 last:border-b-0 text-xs"
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${cls}`} />
        <span className="text-slate-700 dark:text-slate-300">{t(METRIC_I18N[result.metric] || result.metric)}</span>
      </div>
      <span className="font-mono tabular-nums text-slate-600 dark:text-slate-400">
        {t('constraints.requiredLabel')}: <span className="text-slate-900 dark:text-slate-100">{fmtMetric(result.metric, result.required)}</span>
        {' · '}
        {t('constraints.achievedLabel')}: <span className={cls}>{fmtMetric(result.metric, result.achieved)}</span>
      </span>
    </div>
  );
};

const ConstraintsTab = ({ scenario, onApply }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Constraint toggle + value state
  const [values, setValues] = useState({
    max_purchase_price: 1_000_000,
    max_cash_available: 225_000,
    max_down_payment_percent: 25,
    min_down_payment_percent: 5,
    min_monthly_cash_flow: 500,
    min_dscr: 1.20,
    min_cap_rate: 5.0,
    min_cash_on_cash: 6.0,
  });
  const [active, setActive] = useState({
    max_purchase_price: false,
    max_cash_available: true,
    max_down_payment_percent: true,
    min_down_payment_percent: false,
    min_monthly_cash_flow: true,
    min_dscr: true,
    min_cap_rate: false,
    min_cash_on_cash: false,
  });

  const setVal = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const setAct = (k, v) => setActive((s) => ({ ...s, [k]: v }));

  const run = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const constraints = {};
      for (const k of Object.keys(active)) {
        if (active[k] && values[k] !== null && values[k] !== undefined && !Number.isNaN(values[k])) {
          constraints[k] = Number(values[k]);
        }
      }
      const resp = await axios.post(
        `${BACKEND_URL}/api/scenario/constraints`,
        { scenario, constraints, locked: [] },
        { withCredentials: true }
      );
      setData(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || t('constraints.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    if (!data || data.status !== 'achievable' || !data.scenario) return;
    onApply({
      purchase_price: data.scenario.purchase_price,
      annual_income: data.scenario.annual_income,
      down_payment_percent: data.scenario.down_payment_percent,
      interest_rate: data.scenario.interest_rate,
    });
    toast.success(t('constraints.appliedToast'));
  };

  const isAchievable = data?.status === 'achievable';
  const isNotAchievable = data?.status === 'not_achievable';
  const isInvalid = data?.status === 'invalid_constraints';

  return (
    <div
      data-testid="constraints-tab"
      role="region"
      aria-live="polite"
      aria-atomic="false"
      className="space-y-4"
    >
      {/* Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0F1419] p-4">
          <div className="flex items-center gap-2 mb-2">
            <Wallet className="w-4 h-4 text-indigo-500" />
            <h3 className="text-xs uppercase tracking-wider font-semibold text-slate-700 dark:text-slate-300">
              {t('constraints.investmentLimits')}
            </h3>
          </div>
          {HARD_FIELDS.map((f) => (
            <ConstraintRow
              key={f.key}
              field={f}
              active={active[f.key]}
              value={values[f.key]}
              onToggle={(v) => setAct(f.key, v)}
              onChange={(v) => setVal(f.key, v)}
            />
          ))}
        </div>
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0F1419] p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-indigo-500" />
            <h3 className="text-xs uppercase tracking-wider font-semibold text-slate-700 dark:text-slate-300">
              {t('constraints.performanceRequirements')}
            </h3>
          </div>
          {PERF_FIELDS.map((f) => (
            <ConstraintRow
              key={f.key}
              field={f}
              active={active[f.key]}
              value={values[f.key]}
              onToggle={(v) => setAct(f.key, v)}
              onChange={(v) => setVal(f.key, v)}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[11px] text-slate-500 dark:text-slate-400 italic max-w-lg">
          {t('constraints.progressiveHint')}
        </p>
        <Button
          data-testid="constraints-run-btn"
          onClick={run}
          disabled={loading}
          className="h-10 bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('constraints.checking')}</>)
                   : (<><ShieldCheck className="w-4 h-4 mr-2" />{t('constraints.checkConstraints')}</>)}
        </Button>
      </div>

      {error && (
        <p data-testid="constraints-error" className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {/* Invalid constraints */}
      {isInvalid && (
        <div data-testid="constraints-status-invalid"
             className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800 dark:text-red-300 text-sm">{t('constraints.invalidTitle')}</p>
              <ul className="mt-1 text-xs text-red-700 dark:text-red-400 list-disc pl-4 space-y-0.5">
                {(data.errors || []).map((e, i) => (
                  <li key={i}>{e.constraint}: <span className="font-mono">{e.error}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Achievable */}
      {isAchievable && (
        <div data-testid="constraints-status-achievable"
             className="rounded-lg border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20 p-4 space-y-3">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-emerald-800 dark:text-emerald-300">{t('constraints.achievableTitle')}</p>
              <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-0.5">{t('constraints.achievableSubtitle')}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('constraints.scenarioPrice')}</p>
              <p data-testid="constraints-price" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">{fmtMoney(data.scenario.purchase_price)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('constraints.scenarioDp')}</p>
              <p data-testid="constraints-dp" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">{fmtPct(data.scenario.down_payment_percent, 1)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('constraints.scenarioMcf')}</p>
              <p data-testid="constraints-mcf" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">{fmtMoney(data.metrics.monthly_cash_flow)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('constraints.scenarioCash')}</p>
              <p data-testid="constraints-cash" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">{fmtMoney(data.cash_required)}</p>
            </div>
          </div>
          {data.constraint_results?.length > 0 && (
            <div className="bg-white/60 dark:bg-black/20 rounded-md p-2">
              {data.constraint_results.map((r) => (
                <ConstraintResultRow key={r.id} result={r} satisfied={r.satisfied} />
              ))}
            </div>
          )}
          <Button
            data-testid="constraints-apply-btn"
            size="sm"
            variant="outline"
            className="border-emerald-400 dark:border-emerald-700"
            onClick={apply}
          >
            {t('constraints.applyScenario')}
          </Button>
        </div>
      )}

      {/* Not achievable — hard constraints feasible but perf targets unmet */}
      {isNotAchievable && data.reason_code !== 'no_hard_feasible_scenario' && (
        <div data-testid="constraints-status-not-achievable"
             className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 p-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800 dark:text-amber-300">{t('constraints.notAchievableTitle')}</p>
              <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-0.5">{t('constraints.notAchievableSubtitle')}</p>
            </div>
          </div>
          {data.closest_scenario && (
            <div className="bg-white/70 dark:bg-black/20 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400 font-semibold mb-2">
                {t('constraints.closestScenario')}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('constraints.scenarioPrice')}</p>
                  <p data-testid="constraints-closest-price" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                    {fmtMoney(data.closest_scenario.scenario.purchase_price)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('constraints.scenarioDp')}</p>
                  <p className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                    {fmtPct(data.closest_scenario.scenario.down_payment_percent, 1)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('constraints.scenarioMcf')}</p>
                  <p data-testid="constraints-closest-mcf" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                    {fmtMoney(data.closest_scenario.metrics.monthly_cash_flow)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('constraints.scenarioCash')}</p>
                  <p data-testid="constraints-closest-cash" className="font-mono tabular-nums font-semibold text-slate-900 dark:text-slate-100">
                    {fmtMoney(data.closest_scenario.cash_required)}
                  </p>
                </div>
              </div>
            </div>
          )}
          {data.unmet_constraints?.length > 0 && (
            <div data-testid="constraints-unmet-block">
              <p className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400 font-semibold mb-1">
                {t('constraints.unmetTitle')}
              </p>
              <div className="bg-white/60 dark:bg-black/20 rounded-md p-2">
                {data.unmet_constraints.map((r) => (
                  <ConstraintResultRow key={r.id} result={r} satisfied={false} />
                ))}
              </div>
            </div>
          )}
          {data.binding_constraints?.length > 0 && (
            <div data-testid="constraints-binding-block">
              <p className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400 font-semibold mb-1">
                {t('constraints.bindingTitle')}
              </p>
              <ul className="text-xs text-amber-800 dark:text-amber-200 space-y-1">
                {data.binding_constraints.map((b) => (
                  <li key={b} data-testid={`constraints-binding-${b}`} className="flex items-center gap-1.5">
                    <Lock className="w-3 h-3" />
                    {t(BINDING_I18N[b] || 'constraints.bindingGeneric', { code: b })}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* P3C.1 — Hard constraints themselves infeasible: no closest scenario is legitimate. */}
      {isNotAchievable && data.reason_code === 'no_hard_feasible_scenario' && (
        <div data-testid="constraints-status-no-hard-feasible"
             className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800 dark:text-red-300">{t('constraints.noHardFeasibleTitle')}</p>
              <p className="text-[11px] text-red-700 dark:text-red-400 mt-0.5">{t('constraints.noHardFeasibleSubtitle')}</p>
            </div>
          </div>
          {data.binding_constraints?.length > 0 && (
            <div data-testid="constraints-binding-block">
              <p className="text-[10px] uppercase tracking-wider text-red-700 dark:text-red-400 font-semibold mb-1">
                {t('constraints.bindingTitle')}
              </p>
              <ul className="text-xs text-red-800 dark:text-red-200 space-y-1">
                {data.binding_constraints.map((b) => (
                  <li key={b} data-testid={`constraints-binding-${b}`} className="flex items-center gap-1.5">
                    <Lock className="w-3 h-3" />
                    {t(BINDING_I18N[b] || 'constraints.bindingGeneric', { code: b })}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <p className="text-[11px] text-slate-500 dark:text-slate-400 italic">
        {t('constraints.deterministicNote')}
      </p>
    </div>
  );
};

export default ConstraintsTab;
